import React, { useState, useEffect } from "react";
import {
  BookOpenText,
  CalendarDays,
  LockKeyhole,
  Users,
  PlayCircle,
  HelpCircle,
  MessageCircle,
  Settings2,
  UserCog,
  FileCode2,
  ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../shadcn/ui/card";
import { Button } from "../../../shadcn/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../../shadcn/ui/accordion";
import { Input } from "../../../shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import { Textarea } from "../../../shadcn/ui/textarea";
import { Checkbox } from "../../../shadcn/ui/checkbox";
import { Badge } from "../../../shadcn/ui/badge";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import { supportService } from "../../../data/host-dummy-data/support/supportService";
import * as localService from "../../../services/localService";
import logger from "../../../utils/logger";

const HostSupport = () => {
  const [activeTicketTab, setActiveTicketTab] = useState("open");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    topic: "",
    subject: "",
    message: "",
    urgent: false,
  });

  // State for storing API data
  const [categories, setCategories] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({
    open: 0,
    pending: 0,
    closed: 0,
  });
  const [quickResources, setQuickResources] = useState([]);
  const [loading, setLoading] = useState({
    categories: true,
    tutorials: true,
    faqs: true,
    tickets: true,
    resources: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories
        const categoriesRes = await supportService.getCategories();
        setCategories(categoriesRes.data);
        setLoading(prev => ({ ...prev, categories: false }));

        // Fetch tutorials
        const tutorialsRes = await supportService.getTutorials();
        setTutorials(tutorialsRes.data);
        setLoading(prev => ({ ...prev, tutorials: false }));

        // Fetch FAQs
        const faqsRes = await supportService.getFaqs();
        setFaqs(faqsRes.data);
        setLoading(prev => ({ ...prev, faqs: false }));

        // Fetch support tickets
        const currentUser = localService.getUser();
        const userId = currentUser?.id;
        if (!userId) {
          logger.error("No user ID found - user may not be logged in");
          return;
        }
        const ticketsRes = await supportService.getSupportTickets(userId);
        setSupportTickets(ticketsRes.data.tickets);
        setTicketCounts(ticketsRes.data.counts);
        setLoading(prev => ({ ...prev, tickets: false }));

        // Fetch quick resources
        const resourcesRes = await supportService.getQuickResources();
        setQuickResources(resourcesRes.data);
        setLoading(prev => ({ ...prev, resources: false }));
      } catch (error) {
        logger.error("Error fetching support center data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchFilteredTickets = async () => {
      try {
        setLoading(prev => ({ ...prev, tickets: true }));
        const currentUser = localService.getUser();
        const userId = currentUser?.id;
        if (!userId) {
          logger.error("No user ID found - user may not be logged in");
          setLoading(prev => ({ ...prev, tickets: false }));
          return;
        }
        const ticketsRes = await supportService.getSupportTickets(userId, activeTicketTab);
        setSupportTickets(ticketsRes.data.tickets);
        setLoading(prev => ({ ...prev, tickets: false }));
      } catch (error) {
        logger.error("Error fetching filtered tickets:", error);
        setLoading(prev => ({ ...prev, tickets: false }));
      }
    };

    fetchFilteredTickets();
  }, [activeTicketTab]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const currentUser = localService.getUser();
      const userId = currentUser?.id;
      if (!userId) {
        logger.error("No user ID found - user may not be logged in");
        return;
      }
      const response = await supportService.submitSupportTicket(userId, formData);
      logger.log("Ticket submitted:", response.data);

      // Update ticket list with new ticket
      if (response.status === 201) {
        setSupportTickets(prevTickets => [response.data.ticket, ...prevTickets]);
        setTicketCounts(prev => ({
          ...prev,
          open: prev.open + 1,
        }));

        // Reset form
        setFormData({
          fullName: "",
          email: "",
          topic: "",
          subject: "",
          message: "",
          urgent: false,
        });
      }
    } catch (error) {
      logger.error("Error submitting support ticket:", error);
    }
  };

  // Helper function to render icon based on icon name
  const renderIcon = iconName => {
    const iconMap = {
      BookOpenText: <BookOpenText className="w-6 h-6 text-gray-500" />,
      CalendarDays: <CalendarDays className="w-6 h-6 text-gray-500" />,
      LockKeyhole: <LockKeyhole className="w-6 h-6 text-gray-500" />,
      Users: <Users className="w-6 h-6 text-gray-500" />,
      Settings2: <Settings2 className="w-5 h-5 text-blue-600" />,
      UserCog: <UserCog className="w-5 h-5 text-blue-600" />,
      FileCode2: <FileCode2 className="w-5 h-5 text-blue-600" />,
    };
    return iconMap[iconName] || null;
  };

  return (
    <HPageContainer>
      <HHeader />

      <div className="overflow-auto flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overscroll-contain">
        {/* Browse by Category - Full Width */}
        <section className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Browse by Category</h2>
            <Button variant="ghost" className="p-0 text-sm text-blue-600 hover:text-blue-800">
              View all categories <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {loading.categories
              ? Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <Card key={i} className="bg-white shadow-sm animate-pulse">
                      <CardHeader>
                        <div className="flex gap-3 items-center">
                          <div className="w-6 h-6 bg-gray-200 rounded" />
                          <div className="w-32 h-5 bg-gray-200 rounded" />
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-2 w-full h-4 bg-gray-200 rounded" />
                        <div className="w-16 h-3 bg-gray-200 rounded" />
                      </CardContent>
                    </Card>
                  ))
              : categories.map(category => (
                  <Card
                    key={category.title}
                    className="bg-white shadow-sm transition-shadow cursor-pointer hover:shadow-md"
                  >
                    <CardHeader>
                      <div className="flex gap-3 items-center">
                        {renderIcon(category.icon)}
                        <CardTitle className="text-lg font-medium text-gray-700">{category.title}</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-2 text-sm text-gray-600">{category.description}</p>
                      <p className="text-xs text-gray-500">{category.articles} articles</p>
                    </CardContent>
                  </Card>
                ))}
          </div>
        </section>

        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Main Content */}
          <main className="space-y-8 lg:col-span-2">
            {/* Setup Tutorials */}
            <section>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">Setup Tutorials</h2>
                <Button variant="ghost" className="p-0 text-sm text-blue-600 hover:text-blue-800">
                  View all tutorials <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {loading.tutorials
                  ? Array(4)
                      .fill(0)
                      .map((_, i) => (
                        <div key={i} className="overflow-hidden bg-white rounded-lg shadow-sm animate-pulse">
                          <div className="w-full h-40 bg-gray-200" />
                          <div className="p-4">
                            <div className="mb-2 w-3/4 h-5 bg-gray-200 rounded" />
                            <div className="w-full h-4 bg-gray-200 rounded" />
                          </div>
                        </div>
                      ))
                  : tutorials.map(tutorial => (
                      <div
                        key={tutorial.id}
                        className="overflow-hidden bg-white rounded-lg shadow-sm transition-shadow cursor-pointer hover:shadow-md"
                      >
                        <div className="relative">
                          <img src={tutorial.imgSrc} alt={tutorial.title} className="object-cover w-full h-40" />
                          <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20">
                            <PlayCircle className="w-12 h-12 text-white opacity-80 transition-opacity hover:opacity-100" />
                          </div>
                          <Badge
                            variant="secondary"
                            className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-1.5 py-0.5"
                          >
                            {tutorial.duration}
                          </Badge>
                        </div>
                        <div className="p-4">
                          <h3 className="mb-1 font-semibold text-gray-700 text-md">{tutorial.title}</h3>
                          <p className="text-sm text-gray-600">{tutorial.description}</p>
                        </div>
                      </div>
                    ))}
              </div>
            </section>

            {/* Common Issues & FAQs */}
            <section className="p-6 bg-white rounded-lg shadow-sm">
              <div className="flex gap-2 items-center mb-4">
                <HelpCircle className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-800">Common Issues & FAQs</h2>
              </div>
              {loading.faqs ? (
                Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <div key={i} className="py-4 border-b border-gray-100 animate-pulse last:border-b-0">
                      <div className="mb-2 w-3/4 h-5 bg-gray-200 rounded" />
                      <div className="w-full h-4 bg-gray-200 rounded" />
                    </div>
                  ))
              ) : (
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map(faq => (
                    <AccordionItem value={`item-${faq.id}`} key={faq.id}>
                      <AccordionTrigger className="font-medium text-left text-gray-700 hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-gray-600">{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </section>

            {/* Need More Help? */}
            <section className="p-6 bg-white rounded-lg shadow-sm">
              <div className="flex gap-2 items-center mb-2">
                <MessageCircle className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-800">Need More Help?</h2>
              </div>
              <p className="mb-6 text-sm text-gray-600">
                Our support team typically responds within 24 hours during business days. For urgent issues, please mark
                your ticket as urgent.
              </p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="fullName" className="block mb-1 text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <Input
                      type="text"
                      id="fullName"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={e => handleFormChange("fullName", e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <Input
                      type="email"
                      id="email"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={e => handleFormChange("email", e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="topic" className="block mb-1 text-sm font-medium text-gray-700">
                    Topic
                  </label>
                  <Select onValueChange={value => handleFormChange("topic", value)}>
                    <SelectTrigger id="topic">
                      <SelectValue placeholder="Select a topic" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical Issue</SelectItem>
                      <SelectItem value="billing">Billing Inquiry</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="general">General Question</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="subject" className="block mb-1 text-sm font-medium text-gray-700">
                    Subject
                  </label>
                  <Input
                    type="text"
                    id="subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={e => handleFormChange("subject", e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block mb-1 text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <Textarea
                    id="message"
                    rows={5}
                    placeholder="Please provide as much detail as possible..."
                    value={formData.message}
                    onChange={e => handleFormChange("message", e.target.value)}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="urgent"
                    checked={formData.urgent}
                    onCheckedChange={checked => handleFormChange("urgent", checked)}
                  />
                  <label
                    htmlFor="urgent"
                    className="text-sm font-medium text-gray-700 peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Mark as urgent
                  </label>
                </div>
                <div>
                  <Button type="submit" className="w-full text-white sm:w-auto bg-slate-800 hover:bg-slate-700">
                    Submit Ticket
                  </Button>
                </div>
              </form>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="mt-8 space-y-8 lg:col-span-1 lg:mt-0">
            {/* My Support Tickets */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">My Support Tickets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex space-x-1 mb-4 border border-gray-200 rounded-md p-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex-1 text-xs h-8 ${activeTicketTab === "open" ? "bg-slate-100 text-slate-800" : ""}`}
                    onClick={() => setActiveTicketTab("open")}
                  >
                    Open{" "}
                    <Badge variant="secondary" className="ml-1.5 bg-green-100 text-green-700">
                      {ticketCounts.open}
                    </Badge>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex-1 text-xs h-8 ${activeTicketTab === "pending" ? "bg-slate-100 text-slate-800" : ""}`}
                    onClick={() => setActiveTicketTab("pending")}
                  >
                    Pending{" "}
                    <Badge variant="secondary" className="ml-1.5 bg-yellow-100 text-yellow-700">
                      {ticketCounts.pending}
                    </Badge>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`flex-1 text-xs h-8 ${activeTicketTab === "closed" ? "bg-slate-100 text-slate-800" : ""}`}
                    onClick={() => setActiveTicketTab("closed")}
                  >
                    Closed{" "}
                    <Badge variant="secondary" className="ml-1.5 bg-gray-200 text-gray-600">
                      {ticketCounts.closed}
                    </Badge>
                  </Button>
                </div>
                {loading.tickets ? (
                  Array(2)
                    .fill(0)
                    .map((_, i) => (
                      <div key={i} className="pb-4 mb-4 border-b border-gray-100 animate-pulse last:border-b-0">
                        <div className="flex items-start mb-1 space-x-2">
                          <div className="w-12 h-5 bg-gray-200 rounded" />
                          <div className="w-3/4 h-5 bg-gray-200 rounded" />
                        </div>
                        <div className="flex items-center space-x-2 mb-1.5">
                          <div className="w-16 h-4 bg-gray-200 rounded" />
                          <div className="w-16 h-4 bg-gray-200 rounded" />
                        </div>
                        <div className="w-32 h-3 bg-gray-200 rounded" />
                      </div>
                    ))
                ) : (
                  <div className="space-y-4">
                    {supportTickets.length > 0 ? (
                      supportTickets.map(ticket => (
                        <div key={ticket.id} className="pb-4 border-b border-gray-100 last:border-b-0">
                          <div className="flex items-start mb-1 space-x-2">
                            <Badge
                              variant="outline"
                              className="text-xs px-1.5 py-0.5 border-green-500 text-green-600 bg-green-50"
                            >
                              {ticket.status}
                            </Badge>
                            <h4 className="text-sm font-medium leading-tight text-gray-700">{ticket.title}</h4>
                          </div>
                          <div className="flex items-center space-x-2 mb-1.5">
                            {ticket.tags.map(tag => (
                              <Badge key={tag} variant="secondary" className="text-xs px-1.5 py-0.5">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">
                            #{ticket.id} &bull; {new Date(ticket.createdAt).toLocaleDateString()} at{" "}
                            {new Date(ticket.createdAt).toLocaleTimeString("en-US", {
                              hour: "numeric",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="py-4 text-center">
                        <p className="text-sm text-gray-500">No {activeTicketTab} tickets found</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Resources */}
            <Card className="bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">Quick Resources</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {loading.resources
                  ? Array(3)
                      .fill(0)
                      .map((_, i) => (
                        <div key={i} className="flex items-start p-3 space-x-3 animate-pulse">
                          <div className="w-5 h-5 bg-gray-200 rounded" />
                          <div>
                            <div className="mb-1 w-32 h-4 bg-gray-200 rounded" />
                            <div className="w-48 h-3 bg-gray-200 rounded" />
                          </div>
                        </div>
                      ))
                  : quickResources.map(resource => (
                      <div
                        key={resource.id}
                        className="flex items-start p-3 space-x-3 rounded-md transition-colors cursor-pointer hover:bg-slate-50"
                      >
                        {renderIcon(resource.icon)}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700">{resource.title}</h4>
                          <p className="text-xs text-gray-500">{resource.description}</p>
                        </div>
                      </div>
                    ))}
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </HPageContainer>
  );
};

export default HostSupport;
